import * as React from 'react';

import { useToast } from '@longline/aqua-ui/services/Toast/useToast';

import { ISystemProxy } from '../../api/models/ISystem';
import { IUser } from '../../api/models/IUser';
import { SystemListView } from './SystemListView';
import { ISystemFilter } from "../../filters/SystemFilter";
import { SystemApi } from '../../api/services/SystemApi';
import { TResourceState } from '../../types/TResourceState';

interface IProps {
  user: IUser;
  onChange: (data: IUser, forceUpdate: boolean) => void;
  filter: ISystemFilter;
  onClearFilter: () => void;
}

const SystemSelection = (props: IProps) => {

  const toaster = useToast();

  // Systems shown in list:
  const [systems, setSystems] = React.useState<ISystemProxy[]>([]);
  const [systemsReady, setSystemsReady] = React.useState<TResourceState>('loading');

  React.useEffect(() => {
    setSystemsReady('loading');
    SystemApi.listAdmin()
    .then(res => {
      const checkedSystems = res.map(s => ({ 
        ...s, 
        checked: !!props.user.systems.find(us => us.uuid === s.uuid) 
      }));
      // Mark all systems attached to user as 'checked':
      setSystems(checkedSystems);
      // Inform surrounding form of checked statuses:
      props.onChange({ systems: checkedSystems } as unknown as IUser, false);
      setSystemsReady('ready');
    })
    .catch(() => {
      setSystemsReady('failed');
      toaster.toast("Failed to load system list.", { dismissable: true });
    })
  }, []);

  const handleCheck = (data: ISystemProxy[], item: any | true | false) => {
    // `data` is the current filtered list selection. Select all/none
    // applies only to the current selection; items now shown in the list
    // retain their checked status.        
    if(item === true) { // Select all
      data.forEach((filteredSystem: ISystemProxy) => {
        const system = systems.find(s => s.uuid === filteredSystem.uuid);
        if(system) (system as any).checked = true;
      });
    }
    else if(item === false) { // Select none
      data.forEach((filteredSystem: ISystemProxy) => {
        const system = systems.find(s => s.uuid == filteredSystem.uuid);
        if(system) (system as any).checked = false;
      });  
    } else { // Single item
      // Find system by UUID.
      const system = systems.find(s => s.uuid == item.uuid);
      if(!system) return;
      // Toggle system.
      (system as any).checked = !(system as any).checked;
    }

    props.onChange({ systems: systems } as IUser, false);
    setSystems([...systems]);
  }

  return (
    <SystemListView 
      loading={systemsReady === 'loading'}
      systems={systems} 
      filter={props.filter} 
      onClearFilter={props.onClearFilter} 
      onCheck={handleCheck}
    />
  );
}

export { SystemSelection }
